import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import useIsMobile from "../../../app/common/util/useIsMobile";

const MODAL_OPEN_EVENT = "suprol:modal-open";

const isActionTarget = (target) =>
  target &&
  target.closest &&
  target.closest(
    ".order-action-btn, .tick-icon, .cross-icon, .ant-btn, button, .anticon"
  );

const MobileOrderActions = ({ children }) => {
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const toggleRef = useRef(null);
  const panelRef = useRef(null);

  const closePanel = useCallback(() => {
    setExpanded(false);
  }, []);

  const updatePanelPosition = useCallback(() => {
    if (!toggleRef.current) return;
    const rect = toggleRef.current.getBoundingClientRect();
    const panelHeight = 180;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < panelHeight;

    setPanelPos({
      top: openAbove ? undefined : rect.bottom + 8,
      bottom: openAbove ? window.innerHeight - rect.top + 8 : undefined,
      right: Math.max(8, window.innerWidth - rect.right),
      openAbove,
    });
  }, []);

  useEffect(() => {
    if (!expanded) return undefined;

    updatePanelPosition();

    const handleOutsideClick = (event) => {
      const inToggle =
        toggleRef.current && toggleRef.current.contains(event.target);
      const inPanel =
        panelRef.current && panelRef.current.contains(event.target);
      if (!inToggle && !inPanel) {
        closePanel();
      }
    };

    const handleDismiss = () => closePanel();
    const handleModalOpen = () => closePanel();

    document.addEventListener("click", handleOutsideClick);
    window.addEventListener("scroll", handleDismiss, true);
    window.addEventListener("resize", updatePanelPosition);
    window.addEventListener(MODAL_OPEN_EVENT, handleModalOpen);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
      window.removeEventListener("scroll", handleDismiss, true);
      window.removeEventListener("resize", updatePanelPosition);
      window.removeEventListener(MODAL_OPEN_EVENT, handleModalOpen);
    };
  }, [expanded, updatePanelPosition, closePanel]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!expanded) {
      updatePanelPosition();
    }
    setExpanded((open) => !open);
  };

  const handlePanelClick = (e) => {
    e.stopPropagation();
    if (isActionTarget(e.target)) {
      closePanel();
    }
  };

  if (!isMobile) {
    return <div className="order-actions-cell">{children}</div>;
  }

  const panel =
    expanded &&
    panelPos &&
    ReactDOM.createPortal(
      <div
        ref={panelRef}
        className={`mobile-actions-panel mobile-actions-panel--portal${
          panelPos.openAbove ? " mobile-actions-panel--above" : ""
        }`}
        style={{
          top: panelPos.top,
          bottom: panelPos.bottom,
          right: panelPos.right,
        }}
        onClick={handlePanelClick}
      >
        <div className="mobile-actions-panel__header">
          <span className="mobile-actions-panel__title">Actions</span>
          <button
            type="button"
            className="mobile-actions-panel__close"
            onClick={(e) => {
              e.stopPropagation();
              closePanel();
            }}
            aria-label="Close actions menu"
          >
            ×
          </button>
        </div>
        {children}
      </div>,
      document.body
    );

  return (
    <div
      className={`order-actions-cell mobile-actions-cell${
        expanded ? " is-expanded" : ""
      }`}
    >
      <button
        ref={toggleRef}
        type="button"
        className="mobile-actions-toggle"
        onClick={handleToggle}
        aria-expanded={expanded}
        aria-label="Toggle row actions"
      >
        {expanded ? "▲" : "···"}
      </button>
      {panel}
    </div>
  );
};

export default MobileOrderActions;
